import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Mail, Paperclip, Check, X, SkipForward, Inbox, ShieldAlert, ChevronDown, ChevronRight, RefreshCw, Link2, FolderUp,
} from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  useEmailTriage, useEmailTriageCounts, useMagTriageren,
  useApproveSuggestion, useRejectSuggestion, useSkipEmail, useMailOphalen, useBijlagenNaarSharePoint,
  type EmailMessage, type EmailSuggestion, type EmailProcessingStatus,
} from '@/hooks/useEmailTriage';
import { useMailTekst } from '@/hooks/useEmailTriage';
import { KoppelBericht } from '@/components/crm/mail/KoppelBericht';

const SOORT_LABEL: Record<string, string> = {
  note: 'Notitie',
  todo: 'Taak',
  document: 'Document',
  status_update: 'Statuswijziging',
  contact_update: 'Contactwijziging',
  lead: 'Lead',
  skip: 'Overslaan',
};

const FILTERS: { id: string; label: string; statussen: EmailProcessingStatus[] }[] = [
  { id: 'open', label: 'Te beoordelen', statussen: ['pending', 'suggestions_ready', 'partial'] },
  { id: 'afgehandeld', label: 'Afgehandeld', statussen: ['approved', 'rejected'] },
  { id: 'overgeslagen', label: 'Overgeslagen', statussen: ['skipped'] },
];

/** Zekerheid als woord; een kaal getal tussen 0 en 1 zegt niemand iets. */
function zekerheid(confidence: number | null): { label: string; variant: 'default' | 'secondary' | 'outline' } {
  if (confidence == null) return { label: 'Geen inschatting', variant: 'outline' };
  if (confidence >= 0.85) return { label: `Zeker (${Math.round(confidence * 100)}%)`, variant: 'default' };
  if (confidence >= 0.6) return { label: `Waarschijnlijk (${Math.round(confidence * 100)}%)`, variant: 'secondary' };
  return { label: `Onzeker (${Math.round(confidence * 100)}%)`, variant: 'outline' };
}

function SuggestieRij({ suggestie, magBeslissen }: { suggestie: EmailSuggestion; magBeslissen: boolean }) {
  const approve = useApproveSuggestion();
  const reject = useRejectSuggestion();
  const bezig = approve.isPending || reject.isPending;
  const afgehandeld = suggestie.status !== 'pending';
  const zeker = zekerheid(suggestie.confidence);

  return (
    <div className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">{SOORT_LABEL[suggestie.suggestion_type] ?? suggestie.suggestion_type}</Badge>
          <Badge variant={zeker.variant} className="text-xs">{zeker.label}</Badge>
          {afgehandeld && (
            <Badge variant="outline" className="text-xs">
              {suggestie.status === 'approved' ? 'Goedgekeurd' : suggestie.status === 'rejected' ? 'Afgewezen' : 'Bewerkt'}
            </Badge>
          )}
        </div>

        <p className="text-sm">
          Koppelen aan{' '}
          <span className="font-medium">
            {suggestie.target_entity_name ?? 'iets nieuws'}
          </span>
          <span className="text-muted-foreground"> ({suggestie.target_table})</span>
        </p>

        {suggestie.ai_reasoning && (
          <p className="text-xs text-muted-foreground">{suggestie.ai_reasoning}</p>
        )}
      </div>

      {!afgehandeld && (
        <div className="flex shrink-0 gap-2">
          <Button
            size="sm"
            disabled={!magBeslissen || bezig}
            onClick={() => approve.mutate({ suggestionId: suggestie.id })}
          >
            <Check className="mr-1.5 h-4 w-4" />
            Goedkeuren
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!magBeslissen || bezig}
            onClick={() => reject.mutate({ suggestionId: suggestie.id })}
          >
            <X className="mr-1.5 h-4 w-4" />
            Afwijzen
          </Button>
        </div>
      )}
    </div>
  );
}

function BerichtKaart({ bericht, magBeslissen }: { bericht: EmailMessage; magBeslissen: boolean }) {
  const [open, setOpen] = useState(true);
  const [koppelen, setKoppelen] = useState(false);
  const skip = useSkipEmail();
  // Pas ophalen als de dialoog opengaat: 78 berichten tegelijk schoonmaken is
  // zonde als je er een koppelt.
  const { data: schoneTekst } = useMailTekst(koppelen ? bericht.id : null);
  const suggesties = bericht.email_suggestions ?? [];
  const openstaand = suggesties.filter((s) => s.status === 'pending').length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex min-w-0 flex-1 items-start gap-2 text-left"
          >
            {open ? <ChevronDown className="mt-1 h-4 w-4 shrink-0" /> : <ChevronRight className="mt-1 h-4 w-4 shrink-0" />}
            <div className="min-w-0">
              <CardTitle className="truncate text-base">{bericht.subject || '(geen onderwerp)'}</CardTitle>
              <p className="truncate text-sm text-muted-foreground">
                {bericht.from_name || bericht.from_email || 'onbekende afzender'}
                {bericht.received_at &&
                  ` · ${format(new Date(bericht.received_at), 'd MMM yyyy HH:mm', { locale: nl })}`}
              </p>
            </div>
          </button>

          <div className="flex shrink-0 items-center gap-2">
            {bericht.has_attachments && (
              <Badge variant="outline" className="gap-1 text-xs">
                <Paperclip className="h-3 w-3" />
                {bericht.attachment_names?.length ?? ''}
              </Badge>
            )}
            <Badge variant={openstaand > 0 ? 'default' : 'secondary'}>
              {openstaand > 0 ? `${openstaand} open` : 'niets open'}
            </Badge>
          </div>
        </div>
      </CardHeader>

      {open && (
        <CardContent className="space-y-3">
          {bericht.body_preview && (
            <p className="rounded-md bg-muted/50 p-3 text-sm text-muted-foreground">
              {bericht.body_preview}
            </p>
          )}

          {suggesties.length === 0 ? (
            <p className="py-2 text-sm text-muted-foreground">
              Nog geen voorstel voor dit bericht. Koppel het zelf met de knop hieronder, of laat
              de classificatie het doen zodra die aanstaat.
            </p>
          ) : (
            <div className="space-y-2">
              {suggesties.map((s) => (
                <SuggestieRij key={s.id} suggestie={s} magBeslissen={magBeslissen} />
              ))}
            </div>
          )}

          <div className="flex flex-wrap justify-end gap-2 pt-1">
            <Button
              size="sm"
              variant="outline"
              disabled={!magBeslissen}
              onClick={() => setKoppelen(true)}
            >
              <Link2 className="mr-1.5 h-4 w-4" />
              Koppelen aan dossier
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={!magBeslissen || skip.isPending}
              onClick={() => skip.mutate({ emailId: bericht.id })}
            >
              <SkipForward className="mr-1.5 h-4 w-4" />
              Overslaan
            </Button>
          </div>

          <KoppelBericht
            bericht={bericht}
            tekst={schoneTekst}
            open={koppelen}
            onOpenChange={setKoppelen}
          />
        </CardContent>
      )}
    </Card>
  );
}

/**
 * T3.5 - het triage-scherm.
 *
 * De achterkant van de mailmodule staat sinds maart in de database maar is nooit
 * aangeroepen: drie tabellen en drie werkende RPC's, nul rijen. Dit scherm is de
 * voorkant. Zolang de ingest-worker (T3.3) niet draait blijft hij leeg, en dat
 * zegt hij ook met zoveel woorden — een leeg scherm zonder uitleg leest als een
 * kapot scherm.
 */
export default function CRMMailTriage() {
  const [filter, setFilter] = useState(FILTERS[0]);
  const { data: berichten = [], isLoading, isError, error } = useEmailTriage(filter.statussen);
  const { data: tellers = {} } = useEmailTriageCounts();
  const { data: magBeslissen = false } = useMagTriageren();
  const ophalen = useMailOphalen();
  const bijlagen = useBijlagenNaarSharePoint();

  const totaalInDb = Object.values(tellers).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center gap-3">
        <Mail className="h-6 w-6" />
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold">Mail-triage</h1>
          <p className="text-sm text-muted-foreground">
            Voorgestelde koppelingen uit binnengekomen post. Niets wordt gekoppeld zonder jouw klik.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => bijlagen.mutate()}
          disabled={bijlagen.isPending}
          title="Bijlagen van gekoppelde berichten naar de documentmap op SharePoint"
        >
          <FolderUp className="mr-1.5 h-4 w-4" />
          {bijlagen.isPending ? 'Bezig...' : 'Bijlagen opbergen'}
        </Button>
        <Button
          variant="outline"
          onClick={() => ophalen.mutate()}
          disabled={ophalen.isPending}
        >
          <RefreshCw className={`mr-1.5 h-4 w-4 ${ophalen.isPending ? 'animate-spin' : ''}`} />
          {ophalen.isPending ? 'Bezig met ophalen...' : 'Post ophalen'}
        </Button>
      </div>

      {!magBeslissen && (
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertDescription>
            Je kunt meekijken maar niet beslissen. Goedkeuren, afwijzen en overslaan
            zijn voorbehouden aan het CRM-team.
          </AlertDescription>
        </Alert>
      )}

      <Tabs
        value={filter.id}
        onValueChange={(id) => setFilter(FILTERS.find((f) => f.id === id) ?? FILTERS[0])}
      >
        <TabsList>
          {FILTERS.map((f) => {
            const aantal = f.statussen.reduce((som, s) => som + (tellers[s] ?? 0), 0);
            return (
              <TabsTrigger key={f.id} value={f.id}>
                {f.label}
                {aantal > 0 && <span className="ml-1.5 text-xs text-muted-foreground">{aantal}</span>}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
      ) : isError ? (
        <Alert variant="destructive">
          <AlertDescription>
            Fout bij laden: {String((error as any)?.message ?? error)}
          </AlertDescription>
        </Alert>
      ) : berichten.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
            <Inbox className="h-10 w-10 text-muted-foreground" />
            <p className="font-medium">Niets te beoordelen</p>
            <p className="max-w-md text-sm text-muted-foreground">
              {totaalInDb === 0
                ? 'Er is nog geen post opgehaald. De ingest-worker (T3.3) is nog niet actief — dit scherm werkt, er is alleen nog niets binnengekomen.'
                : 'Alles in deze lijst is afgehandeld. Kijk bij de andere tabbladen.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {berichten.map((b) => (
            <BerichtKaart key={b.id} bericht={b} magBeslissen={magBeslissen} />
          ))}
        </div>
      )}
    </div>
  );
}
